import { z } from 'zod';
/** TechSpec §6.4 — metric queries. */
export const METRIC_AGGREGATES = ['avg', 'min', 'max', 'last'];
/** Most points a series may carry; the step is raised until it fits (§6.4). */
export const MAX_POINTS_PER_SERIES = 500;
/** Most series one query returns; `truncated` says there were more. */
export const MAX_SERIES_PER_QUERY = 20;
/** Longest span a query may cover. */
export const MAX_QUERY_SPAN_DAYS = 400;
const TAG_PART = /^[^,:]{1,200}:[^,]{0,200}$/;
/** `host:srv1,path:/` → { host: 'srv1', path: '/' }. Values may contain ':'. */
export const TagFilterSchema = z
    .string()
    .max(1000)
    .optional()
    .transform((value, ctx) => {
    const tags = {};
    for (const part of (value ?? '').split(',').filter(Boolean)) {
        if (!TAG_PART.test(part)) {
            ctx.addIssue({ code: 'custom', message: `not key:value: ${part}` });
            return z.NEVER;
        }
        const at = part.indexOf(':');
        tags[part.slice(0, at)] = part.slice(at + 1);
    }
    return tags;
});
/** The inverse of TagFilterSchema, for clients building a query string. */
export const formatTagFilter = (tags) => Object.entries(tags ?? {})
    .map(([k, v]) => `${k}:${v}`)
    .join(',');
export const MetricQuerySchema = z.object({
    sourceId: z.uuid(),
    name: z.string().min(1).max(200),
    tags: TagFilterSchema,
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    /** Seconds. Raised if it would yield more than MAX_POINTS_PER_SERIES. */
    step: z.coerce.number().int().min(1).max(86_400).optional(),
    agg: z.enum(METRIC_AGGREGATES).default('avg'),
    fn: z.enum(['rate']).optional(),
});
/** [epoch milliseconds, value] — compact on the wire. */
export const MetricPointTupleSchema = z.tuple([z.number(), z.number()]);
export const MetricSeriesSchema = z.object({
    tags: z.record(z.string(), z.string()),
    points: z.array(MetricPointTupleSchema),
});
export const MetricQueryResponseSchema = z.object({
    resolution: z.enum(['raw', 'hourly']),
    step: z.number().int(),
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    truncated: z.boolean(),
    series: z.array(MetricSeriesSchema),
});
export const MetricLatestQuerySchema = z.object({
    sourceId: z.uuid(),
    name: z.string().min(1).max(200),
    tags: TagFilterSchema,
});
export const MetricLatestResponseSchema = z.object({
    items: z.array(z.object({
        tags: z.record(z.string(), z.string()),
        value: z.number(),
        ts: z.iso.datetime({ offset: true }),
    })),
});
export const MetricSeriesListResponseSchema = z.object({
    series: z.array(z.object({
        name: z.string(),
        /** Every tag set seen for this name. */
        tagSets: z.array(z.record(z.string(), z.string())),
    })),
});
const NICE_STEPS = [10, 30, 60, 300, 900, 1800, 3600, 10_800, 21_600, 43_200, 86_400];
/**
 * The bucket width for a span: about 300 points by default, never more than
 * MAX_POINTS_PER_SERIES, snapped to a step a human reads easily (§6.4).
 */
export function chooseStep(spanSeconds, requested) {
    const floor = Math.ceil(spanSeconds / MAX_POINTS_PER_SERIES);
    if (requested !== undefined)
        return Math.max(requested, floor);
    const target = Math.max(spanSeconds / 300, floor);
    return NICE_STEPS.find((s) => s >= target) ?? Math.ceil(target / 86_400) * 86_400;
}
//# sourceMappingURL=metrics.js.map